import React, { useState, useEffect } from 'react';
import { X, MessageSquare } from 'lucide-react';

interface BetaBannerProps {
  completedCount: number;
  totalLimit: number;
  onFeedbackClick?: () => void;
}

const STORAGE_KEY = 'vidly_beta_banner_dismissed';

export const BetaBanner: React.FC<BetaBannerProps> = ({
  completedCount,
  totalLimit,
  onFeedbackClick,
}) => {
  const [dismissed, setDismissed] = useState(true);

  useEffect(() => {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    setDismissed(stored === 'true');
  }, []);

  const handleDismiss = () => {
    sessionStorage.setItem(STORAGE_KEY, 'true');
    setDismissed(true);
  };

  if (dismissed) return null;

  const progress = Math.min((completedCount / totalLimit) * 100, 100);

  return (
    <div className="w-full h-8 bg-[#09090b] border-b border-zinc-800 flex items-center justify-between px-3 sm:px-4 text-[11px] select-none z-50">
      {/* Left: Indicator */}
      <div className="flex items-center gap-1.5 sm:gap-2 min-w-0 shrink-0">
        <span className="relative flex h-2 w-2">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
          <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
        </span>
        <span className="font-semibold tracking-wide text-zinc-300 uppercase text-[10px] sm:text-[11px]">
          Beta
        </span>
      </div>

      {/* Center: Progress */}
      <div className="flex items-center gap-2 mx-2 truncate">
        <div className="w-16 sm:w-28 h-1.5 rounded-full bg-zinc-800 overflow-hidden shrink-0">
          <div
            className="h-full rounded-full bg-emerald-500 transition-all duration-500"
            style={{ width: `${progress}%` }}
          />
        </div>
        <span className="text-zinc-400 whitespace-nowrap text-[10px] sm:text-[11px]">
          <span className="hidden sm:inline">{completedCount}/{totalLimit} vídeos procesados</span>
          <span className="sm:hidden">{completedCount}/{totalLimit} vídeos</span>
        </span>
      </div>

      {/* Right: Feedback & Close */}
      <div className="flex items-center gap-2 sm:gap-3 shrink-0">
        {onFeedbackClick && (
          <button
            onClick={onFeedbackClick}
            className="hidden sm:flex items-center gap-1 text-zinc-400 hover:text-white transition-colors"
          >
            <MessageSquare className="w-3 h-3" />
            <span className="underline underline-offset-2">Dar feedback</span>
          </button>
        )}
        <button
          onClick={handleDismiss}
          className="text-zinc-500 hover:text-zinc-300 transition-colors p-1"
          aria-label="Cerrar banner"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
};
